import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { AppShellRootComponent, injectShellRouteData, type ShellHeader } from '@sbe-spinoff-client/app-shell';
import { AuthService } from '@sbe-spinoff-schoolbell-e/web-shared';
import { LogoutService } from './shared/logout.service';

interface ShellRouteData extends Record<string, unknown> {
  shellMode: ShellHeader;
  /** true면 셸 크롬(toolbar/footer)만 건너뛴다(팝업/콜백 라우트). */
  shellBare: boolean;
}

@Component({
  selector: 'app-root',
  imports: [MatButtonModule, AppShellRootComponent],
  template: `
    <sbe-app-shell-root
      [shellBare]="shellRouteData().shellBare"
      [header]="shellRouteData().shellMode"
      [loggedIn]="auth.isLoggedIn()"
      title="학교종이 개발자센터"
      homeRoute="/apps"
    >
      <!--
        슬롯 하나당 @if 하나씩 — 같은 @if 블록에 서로 다른 슬롯 셀렉터를 가진 root 노드를
        여러 개 두면 Angular가 어느 슬롯에도 투영하지 못한다(NG8011). shellBare(팝업)
        라우트에서는 이 슬롯 콘텐츠가 header='none' 셸에 투영되지 않고 버려지지만
        컴포넌트 자신은 여전히 인스턴스화·변경감지된다 — 조건을 각 @if에 중복해서라도
        shellBare가 아닐 때만 만든다(코드 리뷰 지적, 2026-08-11. 이 앱은 현재
        shellBare:true 라우트가 없어 당장은 무해하지만, 향후 팝업 라우트가 생겼을 때의
        회귀를 미리 막는다).
      -->
      @if (!shellRouteData().shellBare && userName()) {
        <span shellToolbarEnd class="text-sm text-gray-500 dark:text-gray-400">{{ userName() }}</span>
      }
      @if (!shellRouteData().shellBare) {
        <button shellToolbarEnd mat-stroked-button type="button" (click)="logout()">로그아웃</button>
      }
    </sbe-app-shell-root>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  protected readonly auth = inject(AuthService);
  private readonly logoutSvc = inject(LogoutService);

  // 이 앱은 로그인 화면이 없다(auth.guard.ts 참고) — 미로그인이면 셸이 렌더되기 전에
  // window.location.href 풀 페이지 리다이렉트로 schoolbell-e-web으로 빠져나간다. 그래서
  // 셸이 그려지는 시점엔 이미 인증이 보장돼 있어, sms-user-web과 달리 shellMode를
  // 라우트마다 다르게 줄 필요가 없다(랜딩/로그인 화면 자체가 없음). `injectShellRouteData()`는
  // 그래도 워크스페이스 공통 계약을 유지하기 위해 쓰되, 팝업 라우트가 생기는 미래를 대비해
  // shellBare만 열어 둔다.
  protected readonly shellRouteData = injectShellRouteData<ShellRouteData>({
    shellMode: 'toolbar',
    shellBare: false,
  });

  readonly userName = computed(() => this.auth.session()?.name ?? null);

  logout(): void {
    this.logoutSvc.logout();
  }
}
